import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, MapPin, PhoneCall, FileText, ImagePlus, Save, Globe, X } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import BizPhotoManager from "@/components/BizPhotoManager";

export default function BusinessContent() {
  const { t } = useLang();
  const [biz, setBiz] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master/business").then((r) => setBiz(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  if (!biz) return null;
  const set = (k, v) => setBiz({ ...biz, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/master/business", biz);
      setBiz(data);
      toast.success(t("savedOk"));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const setNum = (i, v) => {
    const nums = [...(biz.contact_numbers || [])];
    nums[i] = v.replace(/\D/g, "").slice(0, 10);
    set("contact_numbers", nums);
  };

  const textField = (key, labelKey, testid) => (
    <div>
      <label className="sf-label">{t(labelKey)}</label>
      <input data-testid={testid} className="sf-input" value={biz[key] || ""} onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  return (
    <div className="max-w-4xl space-y-6" data-testid="business-content-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Globe className="h-5 w-5 text-emerald-700" /> {t("bizContentTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("bizContentSub")}</p>
        </div>
        <button data-testid="save-business-btn" className="sf-btn-primary" onClick={save} disabled={busy}>
          <Save className="h-4 w-4" /> {busy ? "..." : t("saveChanges")}
        </button>
      </div>

      <div className="sf-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <Building2 className="h-4 w-4" /> {t("sectionIdentity")}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {textField("business_name", "businessNameLabel", "biz-name-input")}
          {textField("trade_name", "tradeName", "biz-trade-input")}
          {textField("owner_name", "legalName", "biz-owner-input")}
          {textField("business_type", "businessType", "biz-type-input")}
          {textField("category", "businessCategory", "biz-category-input")}
          {textField("license_no", "licenseNo", "biz-license-input")}
          {textField("udyam_no", "udyamNo", "biz-udyam-input")}
          {textField("gstin", "gstin", "biz-gstin-input")}
        </div>
      </div>

      <div className="sf-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <MapPin className="h-4 w-4" /> {t("sectionAddress")}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {textField("stall_no", "store", "biz-stall-input")}
          {textField("landmark", "location", "biz-landmark-input")}
          {textField("district", "district", "biz-district-input")}
          {textField("state", "state", "biz-state-input")}
          {textField("pin", "pin", "biz-pin-input")}
          {textField("country", "country", "biz-country-input")}
        </div>
        <div>
          <label className="sf-label">{t("mapsLinkLabel")}</label>
          <input data-testid="biz-maps-input" className="sf-input font-mono-sf !text-sm" value={biz.maps_link || ""} onChange={(e) => set("maps_link", e.target.value)} />
        </div>
      </div>

      <div className="sf-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <PhoneCall className="h-4 w-4" /> {t("sectionContacts")}
        </div>
        <label className="sf-label">{t("contactNumbersLabel")}</label>
        <div className="space-y-2">
          {(biz.contact_numbers || []).map((n, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-sm font-bold text-slate-500">+91</span>
              <input
                data-testid={`biz-contact-${i}`}
                className="sf-input font-mono-sf"
                inputMode="numeric"
                value={n}
                onChange={(e) => setNum(i, e.target.value)}
              />
              {(biz.contact_numbers || []).length > 1 && (
                <button data-testid={`biz-contact-remove-${i}`} className="p-2 text-rose-600 hover:bg-rose-50 rounded-md transition-colors" onClick={() => set("contact_numbers", biz.contact_numbers.filter((_, j) => j !== i))} aria-label={t("removeContact")}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button data-testid="biz-contact-add-btn" className="sf-link text-sm" onClick={() => set("contact_numbers", [...(biz.contact_numbers || []), ""])}>
          + {t("addContact")}
        </button>
      <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-slate-200">
        <div>
          <label className="sf-label">WhatsApp Group Link</label>
          <input data-testid="biz-whatsapp-input" className="sf-input font-mono-sf !text-sm" placeholder="https://chat.whatsapp.com/..." value={biz.whatsapp_link || ""} onChange={(e) => set("whatsapp_link", e.target.value)} />
        </div>
        <div>
          <label className="sf-label">Facebook Group Link</label>
          <input data-testid="biz-facebook-input" className="sf-input font-mono-sf !text-sm" placeholder="https://www.facebook.com/groups/..." value={biz.facebook_link || ""} onChange={(e) => set("facebook_link", e.target.value)} />
        </div>
      </div>
       <div className="pt-3 border-t border-slate-200">
         <label className="sf-label">Customer Contact Number</label>
         <select data-testid="customer-contact-number" className="sf-input" value={biz.customer_contact_number || ""} onChange={(e) => set("customer_contact_number", e.target.value)}>
           <option value="">Select a contact number</option>
           {(biz.contact_numbers || []).filter((n) => /^\d{10}$/.test(n)).map((n, i) => (
             <option key={i} value={n}>+91 {n}</option>
           ))}
         </select>
         <p className="text-xs text-slate-500 mt-1">This number will be used when customers choose Contact Dealer.</p>
       </div>
      </div>

      <div className="sf-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <FileText className="h-4 w-4" /> {t("sectionContent")}
        </div>
        <div>
          <label className="sf-label">{t("descriptionLabel")}</label>
          <textarea data-testid="biz-description-input" className="sf-input min-h-28" value={biz.description || ""} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div>
          <label className="sf-label">{t("announcementLabel")}</label>
          <textarea data-testid="biz-announcement-input" className="sf-input min-h-16" placeholder={t("announcementPh")} value={biz.announcement || ""} onChange={(e) => set("announcement", e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input data-testid="biz-announcement-active" type="checkbox" className="h-4 w-4" checked={!!biz.announcement_active} onChange={(e) => set("announcement_active", e.target.checked)} />
          {t("showAnnouncement")}
        </label>
      </div>

      <div className="sf-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900">
          <ImagePlus className="h-4 w-4" /> {t("sectionPhotos")}
        </div>
        <BizPhotoManager biz={biz} onChange={setBiz} allowLogo />
      </div>

      <button data-testid="save-business-btn-bottom" className="sf-btn-primary w-full !py-4" onClick={save} disabled={busy}>
        <Save className="h-5 w-5" /> {busy ? "..." : t("saveChanges")}
      </button>
    </div>
  );
}
